import React, { useState } from 'react';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { apiLogin } from '../utils/api';

// localStorage keys shared across all three token pages (same origin), so one
// login unlocks /payu, /irena and /aifx together and survives reloads.
export const AUTH_FLAG = 'payubot_authed';
export const PW_KEY = 'payubot_pw';

// Full-screen gate. Renders the app only after the server validates the
// credentials. The password lives in localStorage (to authorize trades) but is
// never hardcoded in the bundle — the server holds the real value.
const LoginGate = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await apiLogin({ username, password });
      // Store the password so /api/trade can be authorized, and mark the session.
      try {
        localStorage.setItem(PW_KEY, JSON.stringify(password));
        localStorage.setItem(AUTH_FLAG, '1');
      } catch (err) { /* storage disabled — session stays in memory only */ }
      onSuccess(password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-gray-800 border border-gray-700 rounded-2xl p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mb-3">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Admin Login</h1>
        </div>

        <label className="block text-xs text-gray-400 mb-1">Username</label>
        <div className="relative mb-4">
          <User className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full bg-gray-900 text-white pl-9 pr-3 py-2.5 rounded-xl border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoComplete="username"
          />
        </div>

        <label className="block text-xs text-gray-400 mb-1">Password</label>
        <div className="relative mb-4">
          <Lock className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-gray-900 text-white pl-9 pr-10 py-2.5 rounded-xl border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoComplete="current-password"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        <button
          type="submit"
          disabled={busy || !password}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {busy ? 'Checking...' : 'Login'}
        </button>
      </form>
    </div>
  );
};

export default LoginGate;
